export const landingEn = {
  landing: {
    heroTitle: "From satellite data to defensible field reports.",
    heroSubtitle: "Sentinel Crop combines Sentinel-2 monitoring, FAO-56 water-demand modelling and data-readiness checks into transparent field reports.",
    getStarted: "Get Started",
    learnMore: "Learn More",
    features: "Features",
    pricing: "Pricing",
    contact: "Contact",
    trustedBy: "Powered by official satellite and weather data",
    feature1Title: "Satellite Monitoring",
    feature1Desc: "Real-time vegetation indices from Sentinel-2 satellites",
    feature2Title: "AI-Powered Insights",
    feature2Desc: "Machine learning predictions for crop health and yield",
    feature3Title: "Smart Irrigation",
    feature3Desc: "FAO-56 based water management optimization",
    feature4Title: "Frost Protection",
    feature4Desc: "Advanced prediction with SMS alerts",
    ctaTitle: "Ready to optimize your farm?",
    ctaButton: "Explore the platform"
  },
  hero: {
    eyebrow: "Field evidence and reporting platform",
    title: "Farm Operations Agent for large-scale agriculture.",
    subtitle: "Prioritize irrigation, detect crop risk, plan field tasks and verify parcel decisions from satellite, weather and field data.",
    secondary: "Water stress. Pest risk. Field tasks. Verified reports.",
    overlayIrrigationPriority: "Irrigation priority",
    overlayWaterStress: "Water stress risk",
    overlayFieldTask: "Field task generated",
    overlayVerifiedReport: "Verified report",
    satelliteMonitoring: "Satellite Monitoring",
    satelliteMonitoringDesc: "Real NDVI crop health monitoring from space",
    frostProtection: "Frost Protection",
    frostProtectionDesc: "Early frost warnings 2-3 days in advance",
    smartIrrigation: "Smart Irrigation",
    smartIrrigationDesc: "FAO-56 based irrigation support",
    startFreeTrial: "View sample report",
    viewSampleReport: "View sample report",
    bookDemo: "Book a demo",
    talkToSales: "Book a demo",
    faoBadge: "Benchmarked",
    chipEvidence: "Audit-support evidence trail",
    chipSentinel: "Sentinel-2 imagery",
    chipFao: "FAO-56 water-demand model",
    chipReadiness: "Data readiness checks"
  },
  featuresSection: {
    badge: "Capabilities",
    title: "Everything needed to generate a defensible field report.",
    subtitle: "Sentinel Crop turns satellite, weather and field data into parcel-level agronomic decisions and defensible reports.",
    healthyVineyard: "Healthy Vineyard",
    waterStressDetected: "Water Stress Detected",
    realTimeMonitoring: "Parcel-level evidence comparison",
    dragSlider: "Drag the slider to see how satellite monitoring detects stress before visible symptoms appear",
    advancedProtection: "Evidence & Reporting Layer",
    howWeMonitor: "From satellite data to defensible field reports",
    howWeMonitorDesc: "Each capability feeds a single, traceable evidence trail per parcel — powering Field Intelligence Reports for cooperatives, producers and insurers.",
    satelliteCropMonitoring: "Parcel Evidence",
    satelliteCropMonitoringDesc: "Sentinel-2 NDVI / NDWI / NDRE per parcel, timestamped and auditable — the objective ground-truth layer behind every report.",
    satelliteDetails: ["Sentinel-2 imagery refreshed every 3–5 days", "Per-parcel NDVI / NDWI / NDRE history", "Cloud filtering and quality flags", "SHA-256 hashed evidence trail (tamper-evident)"],
    frostProtectionAlerts: "Portfolio Risk Alerts",
    frostProtectionAlertsDesc: "Frost, drought, water-stress and anomaly signals across an entire portfolio of parcels — designed for cooperative technicians and insurance ops.",
    frostDetails: ["Frost forecast 2–3 days ahead", "Drought & water-stress flags", "Cross-parcel anomaly detection", "API + email/SMS delivery"],
    smartIrrigation: "FAO-56 Water Demand",
    smartIrrigationDesc: "ET₀ via Penman-Monteith and Dual Kc water balance per parcel. Standardised methodology, transparent inputs, defensible outputs.",
    irrigationDetails: ["FAO-56 Penman-Monteith ET₀", "Dual Kc soil water balance", "Benchmarked against reference ET₀ datasets", "Inputs and assumptions fully exposed"],
    digitalFieldNotebook: "Intervention Traceability",
    digitalFieldNotebookDesc: "Every irrigation, treatment and fertilisation event linked to its parcel and to the satellite evidence on the same date.",
    fieldNotebookDetails: ["Bidirectional link to prescriptions", "Operator and timestamp captured", "Cross-checked against NDVI series", "Exportable for audits and compliance reviews"],
    fertilizationPlanning: "Nutrient Planning Evidence",
    fertilizationPlanningDesc: "VRA nitrogen, P and K plans built from NDVI/NDRE zones and soil data — with the underlying data attached to every recommendation.",
    fertilizationDetails: ["K-Means sub-parcel zones at 10 m", "N / P / K sufficiency indices", "Shapefile / CSV export to machinery", "Methodology and inputs disclosed"],
    professionalReports: "Defensible Field Reports",
    professionalReportsDesc: "Field Intelligence Reports that consolidate satellite, FAO-56 and intervention data into one timestamped, hashed PDF per parcel.",
    reportsDetails: ["One report per parcel, per period", "Sources, methods and assumptions listed", "SHA-256 Document ID with public verification of non-sensitive metadata", "EN / ES, ready for cooperatives & insurers"]
  },
  pricingSection: {
    badge: "Transparent Pricing",
    title: "Plans for evidence-driven operations",
    subtitle: "Field Intelligence Reports, parcel-level evidence, FAO-56 water-demand modelling, portfolio monitoring and API / white-label access — built for cooperatives, insurers, agribusiness and 200+ ha producers.",
    monthly: "Monthly",
    annual: "Annual",
    save20: "💰 Save 20%",
    mostPopular: "⭐ Most Popular Choice",
    hectares: "Hectares:",
    costPerHa: "Cost per ha:",
    startFreeTrial: "Explore the platform",
    requestAccess: "Start 7-day trial",
    talkToSales: "Talk to sales",
    requestApiDemo: "Request API demo",
    freeTrial: "🎁 Free tier available",
    trialBadge: "",
    noCreditCard: "No credit card required",
    viewCompleteFeatures: "📋 View Complete Feature List",
    allPricesExclude: "All prices exclude VAT. Annual plans offer 8.3% savings. Enterprise solutions available upon request.",
    highResAvailable: "High-resolution satellite imagery available on demand for detailed parcel analysis",
    freeTierLink: "Just exploring? Access the free tier →",
    plans: {
      producer: {
        name: "Producer",
        subtitle: "For technical producers and farms up to 250 ha",
        description: "For technical producers and medium-to-large farms (up to 250 ha) who need parcel-level evidence and FAO-56 water-demand modelling consolidated into Field Intelligence Reports.",
        priceNote: "EUR/month, billed annually"
      },
      cooperative: {
        name: "Cooperative",
        subtitle: "50–500 members on one dashboard",
        description: "Cooperative workflow: portfolio monitoring across all members, anonymous benchmarking and Field Intelligence Reports per parcel for subsidy, traceability and sustainability audits.",
        priceLabel: "Custom",
        priceNote: "Volume pricing"
      },
      enterprise: {
        name: "Enterprise",
        subtitle: "Insurers · Traders · Agribusiness",
        description: "Insurance and agribusiness workflow: portfolio risk monitoring, parcel-level evidence and Field Intelligence Reports delivered via REST API and white-label, integrated into your core systems.",
        priceLabel: "Custom",
        priceNote: "API access · SLA"
      }
    },
    contactForm: {
      cooperativeTitle: "Talk to sales — Cooperative",
      cooperativeSubtitle: "Tell us about your cooperative. We will respond within 24 business hours.",
      enterpriseTitle: "Request Enterprise / API demo",
      enterpriseSubtitle: "Insurance, agribusiness or trading? We will reach out within 24 business hours.",
      name: "Full name",
      organization: "Organization",
      email: "Work email",
      phone: "Phone (optional)",
      membersOrHectares: "Number of members / hectares",
      useCase: "Use case",
      useCaseInsurance: "Insurance",
      useCaseCooperative: "Cooperative",
      useCaseAgribusiness: "Agribusiness / Trader",
      useCaseOther: "Other",
      submit: "Send inquiry",
      submitting: "Sending…",
      successTitle: "Thank you",
      successMessage: "We will contact you within 24 business hours.",
      errorTitle: "Could not send",
      errorMessage: "Please try again or email hello@sentinelcrop.com directly.",
      requiredFields: "Please complete all required fields with a valid email."
    },
    traceabilityFeature: "SHA-256 Document ID with public verification of non-sensitive metadata",
    traceabilityDetailed: "SHA-256 Document ID with public verification of non-sensitive metadata. Optional public anchoring applies only to selected certificates."
  },
  footerSection: {
    platform: "Platform",
    pricing: "Pricing",
    sampleReport: "Sample Report",
    resources: "Resources",
    dataSources: "Data Sources",
    standardsMethodologies: "Standards & Methodologies",
    support: "Support",
    helpCenter: "Help Center",
    contactUs: "Contact",
    bookDemo: "Book a Demo",
    login: "Login",
    companyDesc: "Sentinel Crop is the Farm Operations Agent for large-scale agriculture — an autonomous agronomic layer that reviews every parcel signal, flags missing evidence and turns validated data into operational decisions.",
    globalSupport: "24/7 Global Support",
    compliance: "Compliance",
    regulatoryReadiness: "Regulatory Readiness",
    privacy: "Privacy",
    terms: "Terms",
    copyright: "© 2026 Sentinel Crop. All rights reserved.",
    cookies: "Cookies",
    legalNotice: "Legal Notice",
    regionsLine: "🌍 Europe • 🌎 Latin America • 🌏 Worldwide Coverage"
  },
  coreStrengths: {
    badge: "Core Technology",
    title: "Three layers. One field report.",
    subtitle: "Crop condition, water demand and report readiness — combined per parcel.",
    satelliteTitle: "Crop condition",
    satelliteDesc: "Sentinel-2 imagery helps track vegetation status, NDVI trends and parcel-level changes.",
    satelliteHighlights: ["NDVI / NDWI per parcel", "Updates every few days"],
    weatherTitle: "Water demand",
    weatherDesc: "FAO-56 estimates ET₀, Kc and ETc to support irrigation decisions.",
    weatherHighlights: ["FAO-56 Penman-Monteith", "Dual Kc by crop stage"],
    algorithmsTitle: "Report readiness",
    algorithmsDesc: "The report shows what data exists, what is missing and what actions are recommended.",
    algorithmsHighlights: ["Declared data limitations", "Recommended next actions"]
  },
  workflow: {
    badge: "Product workflow",
    title: "Monitor → Decide → Report → Prove",
    subtitle: "One workflow that turns satellite, weather and field data into parcel-level decisions and defensible reports.",
    steps: {
      monitor: {
        title: "Monitor",
        desc: "Understand what is happening in each parcel using satellite, weather, alerts and field observations."
      },
      decide: {
        title: "Decide",
        desc: "Turn agronomic signals into irrigation, fertilization, scouting and treatment-support decisions."
      },
      report: {
        title: "Report",
        desc: "Generate parcel-level reports that disclose data availability, recommendations and limitations."
      },
      prove: {
        title: "Prove",
        desc: "Preserve defensible evidence with satellite timestamps, field records and SHA-256 document traceability."
      }
    }
  },
  sampleReport: {
    badge: "Sample output",
    title: "Sample Field Intelligence Report",
    subtitle: "What a Sentinel Crop report looks like for one parcel — combining satellite condition, water demand and declared data readiness.",
    parcelTitle: "Parcel overview",
    parcelLine1: "Finca La Conchuela · Olive grove · 12.4 ha",
    parcelLine2: "Coords 37.85°N, -4.78°W · Reporting period: last 90 days",
    ndviTitle: "NDVI summary",
    ndviLine1: "Mean NDVI 0.62 · 12 cloud-free Sentinel-2 acquisitions",
    ndviLine2: "Stable vigour, mild stress in north-east sub-zone",
    faoTitle: "FAO-56 water demand",
    faoLine1: "ET₀ 4.8 mm/day · Kc 0.65 · ETc 3.1 mm/day",
    faoLine2: "Estimated weekly irrigation requirement: 18–22 mm",
    readinessTitle: "Data readiness",
    readinessLine1: "NDVI · FAO-56 · Soil analysis: AVAILABLE",
    readinessLine2: "Alerts evaluation: PENDING — partial report",
    recsTitle: "Agronomic recommendations",
    recsLine1: "Inspect north-east sub-zone for water stress",
    recsLine2: "Run alert evaluation to upgrade to full report",
    disclaimerTitle: "Professional disclaimer",
    disclaimerLine1: "Decision-support output based on Sentinel-2 and FAO-56 modelling.",
    disclaimerLine2: "Does not replace professional agronomic advice.",
    footnote: "Illustrative example. Actual reports use real Sentinel-2 acquisitions and FAO-56 calculations for the selected parcel.",
    verificationNote: "Sample reports include a Document ID that can be checked on /verify/report."
  },
  ndviShowcase: {
    badge: "Sentinel-2 Satellite Technology",
    title: "Parcel-level satellite evidence",
    subtitle: "Sentinel-2 NDVI imagery exposes per-parcel variation as auditable evidence — the objective layer behind every Field Intelligence Report.",
    resolution: "Resolution",
    resolutionValue: "10m pixels",
    updateFrequency: "Update Frequency",
    updateFrequencyValue: "Every 5 days",
    accuracy: "Accuracy",
    benchmark: "Benchmarking",
    benchmarkValue: "FAO-56 selected runs",
    professionalTitle: "Professional Satellite Monitoring",
    professionalDesc: "Our platform processes Sentinel-2 satellite data to generate accurate NDVI maps. Green zones indicate healthy vegetation, while red/orange areas reveal stress from drought, disease, or nutrient deficiency.",
    detectTitle: "What You Can Detect:",
    detectItems: ["Water stress before visible wilting", "Nutrient deficiencies in specific zones", "Disease outbreaks in early stages", "Irrigation system malfunctions"],
    liveLabel: "Live · Sentinel-2 time-lapse"
  },
  earlyAccess: {
    badge: "Now Available",
    title: "Request pilot access to the evidence layer",
    subtitle: "Onboard your cooperative, portfolio or operation to Sentinel Crop's parcel-level evidence and FAO-56 reporting stack.",
    algorithms: "Algorithms",
    satelliteIndices: "Satellite Indices",
    validatedAccuracy: "Benchmark Metrics",
    startTrial: "Request pilot access",
    viewFeatures: "View All Features",
    noCreditCard: "Free tier available",
    realData: "Real satellite data",
    discount: "50% early adopter discount",
    trust: "Algorithms benchmarked against satellite imagery and weather reference datasets; field validation in progress."
  },
  advancedCapabilities: {
    badge: "Advanced Capabilities",
    title: "Beyond Basic Monitoring",
    subtitle: "Advanced capabilities beyond basic monitoring — modules in active development",
    fire: { title: "Fire Detection", desc: "NASA FIRMS + EFFIS multi-source alerts" },
    ml: { title: "Anomaly Detection", desc: "Machine learning for agronomic risk (Beta)" },
    phenology: { title: "Phenology & GDD", desc: "Growth degree days tracking" },
    harvest: { title: "Harvest Estimator", desc: "Agronomic yield prediction v2.0" },
    vra: { title: "VRA Maps", desc: "Variable rate fertilization zones" },
    rotation: { title: "AI Crop Rotation", desc: "Smart rotation with soil rules" },
    passport: { title: "Parcel Passport", desc: "Farm traceability and compliance records" },
    foliar: { title: "Foliar OCR Analysis", desc: "Lab report scanning & analysis" }
  },
  roiCalculator: {
    badge: "Water Savings Calculator",
    title: "Estimate potential water savings",
    subtitle: "Estimate potential water and cost savings using a FAO-56 ET₀ engine and conservative literature assumptions. Indicative figures, not a guaranteed return.",
    yourFarm: "Your Farm Details",
    cropType: "Crop Type",
    region: "Region",
    hectares: "Farm Size",
    methodology: "ET₀ FAO-56 algorithm (R²=0.94 internal coherence, n=472 obs, Open-Meteo reference). Conservative 20% savings estimate.",
    yourSavings: "Your Estimated Savings",
    savedPerYear: "saved per year",
    cubicMetersSaved: "m³ water saved/year",
    kgCO2Avoided: "kg CO₂ avoided/year",
    roiLabel: "Estimated return vs plan cost:",
    roiDescription: "{{roi}}% return on €588/year investment",
    cta: "Explore the platform",
    disclaimer: "Indicative estimate based on generic FAO-56 literature. Real savings depend on each farm and have not been independently measured by Sentinel Crop. Not a guaranteed return."
  },
  idealCustomer: {
    badge: "Who it's for",
    title: "Who is Sentinel Crop for?",
    subtitle: "Different teams need different evidence. Sentinel Crop helps farms, cooperatives, insurers and advisors turn parcel data into decision-support reports.",
    farms: {
      title: "Farms & cooperatives",
      headline: "Reports that help explain why an agronomic decision was made.",
      body: "Document irrigation, fertilization, scouting and treatment-support decisions with satellite, weather and field evidence.",
      useCases: ["Irrigation review", "Fertilization planning", "Scouting prioritization", "Member reporting"],
      cta: "Explore pilot"
    },
    insurers: {
      title: "Agricultural insurers",
      headline: "Reports that help document parcel conditions after an insured agricultural event.",
      body: "Review satellite timestamps, rainfall anomalies, NDVI/NDWI trends, field records and data limitations as supporting evidence for claim review.",
      useCases: ["Post-event review", "Before / after evidence", "Remote claim triage", "Supporting documentation"],
      disclaimer: "Decision-support evidence only. Not an official loss assessment.",
      cta: "Discuss post-event review"
    },
    advisors: {
      title: "Agronomic advisors",
      headline: "Decision-support reports that turn parcel signals into clear next steps.",
      body: "Prioritize which parcels need irrigation review, fertilization adjustment, scouting or targeted field validation.",
      useCases: ["Technical recommendations", "Field visit prioritization", "Treatment-support review", "Client-ready reports"],
      cta: "Talk to sales"
    }
  },
  problem: {
    badge: "The problem",
    title: "Agricultural decisions still need stronger evidence.",
    subtitle: "Cooperatives often need stronger evidence behind member-reported field conditions. Insurers need faster ways to review parcel conditions after an agricultural event. Producers still make irrigation decisions with incomplete water-demand data.",
    items: [
      {
        metric: "$1.25B",
        label: "estimated market size for satellite-based claim verification — third-party industry estimate, indicative only.",
        source: "Industry estimate, 2025 — indicative figure"
      },
      {
        metric: "44%",
        label: "of global irrigation water withdrawals are estimated to exceed actual crop requirements, according to FAO references on irrigation water use.",
        source: "FAO AQUASTAT references on irrigation water use"
      },
      {
        metric: "60%",
        label: "of global irrigated cropland is estimated to operate under high water stress, according to FAO sustainable-irrigation references.",
        source: "FAO sustainable irrigation references"
      }
    ],
    sourcesFootnote: "Sources: FAO AQUASTAT references · FAO sustainable irrigation references · industry estimates. Figures are indicative and provided for context only."
    ,
    painPointsTitle: "What this looks like in the field",
    painPoints: [
      { title: "Member-reported conditions", desc: "Cooperatives often need additional satellite or field evidence to support member declarations." },
      { title: "Post-event parcel review", desc: "Insurers benefit from faster ways to review parcel conditions after an agricultural event." },
      { title: "Incomplete water-demand data", desc: "Producers still schedule irrigation with limited ET₀ or soil-moisture context per parcel." }
    ]
  },
  faq: {
    badge: "Enterprise FAQ",
    title: "What buyers ask before signing.",
    subtitle: "Direct answers to the questions cooperatives, insurers and large producers raise during procurement.",
    talkToSales: "Talk to sales",
    items: [
      {
        q: "How does Sentinel Crop integrate with our existing ERP, GIS or claims system?",
        a: "We expose a documented REST API with parcel-level endpoints (NDVI, ET₀, water status, alerts, certificates) and standard exports (GeoJSON, Shapefile, CSV, PDF). Webhooks and direct ERP / claims-system connectors are available for Enterprise pilots and custom implementation, scoped per customer."
      },
      {
        q: "Where is our data stored and how is it protected?",
        a: "Data is stored on audited cloud infrastructure (default: EU region; other regions on request), encrypted at rest and in transit. Row-level security per organization. GDPR-oriented data processing terms available for enterprise customers. Optional anchoring of certificate hashes (no personal data) to a public ledger for tamper-evident proof — your raw data never leaves our infrastructure."
      },
      {
        q: "How long does pilot onboarding take for a cooperative with 200+ members?",
        a: "Pilot onboarding timelines depend on parcel volume, data quality and required integrations. Typical phases include parcel ingestion (KML, shapefile, GeoJSON or bulk import from local parcel registries where available), user provisioning and role configuration, and optional ERP integration. We define a concrete plan with each cooperative during scoping."
      },
      {
        q: "Can the evidence be used in audits, claim disputes or board reporting?",
        a: "Generated reports include a SHA-256 Document ID. Third parties can verify whether the Document ID was issued by Sentinel Crop and view non-sensitive metadata such as report type, generation date, included sections and declared missing sections. Reports are supporting evidence and do not replace official audit, legal, insurance or compliance decisions."
      },
      {
        q: "What is the support model for Enterprise customers?",
        a: "Dedicated account manager, periodic business reviews and agronomist support for claim review workflows. Uptime targets, response times and white-label deployment are available for Enterprise pilots and custom implementation, defined contractually per customer."
      },
      {
        q: "How is pricing structured for portfolios above 10,000 hectares?",
        a: "Volume-based pricing with declining cost per hectare above 5,000 ha. Insurance and cooperative tiers include unlimited users, API access and white-label reports. Contact sales for a tailored quote based on your portfolio profile and integration needs."
      },
      {
        q: "Can we white-label the platform for our members or insureds?",
        a: "Yes, on Enterprise plan. Custom domain, your logo and color palette, your terms of service. Member-facing dashboard with restricted scope. We remain the technical operator behind the scenes."
      },
      {
        q: "What happens to our historical data if we leave?",
        a: "Full data export on request: raw measurements, certificates, reports in open formats (GeoJSON, CSV, PDF). 90-day grace period after contract end. No vendor lock-in by design — your parcels and history belong to you."
      }
    ]
  },
  builtOn: {
    badge: "Built on",
    title: "Open data, audited infrastructure",
    subtitle: "We don't reinvent the wheel — we stand on the shoulders of public Earth-observation programs, global weather networks and audited cloud infrastructure.",
    disclaimer: "All names and trademarks are the property of their respective owners. Sentinel Crop is an independent platform and is not affiliated with, endorsed by, or sponsored by any of the organizations listed above.",
    sources: [
      { name: "Sentinel-1 / 2", category: "Radar & optical imagery" },
      { name: "Landsat 8 / 9", category: "Global multispectral" },
      { name: "Global Climate Reanalysis", category: "Long-term weather data" },
      { name: "Multi-source Forecast APIs", category: "Worldwide weather" },
      { name: "Polygon PoS (optional)", category: "Tamper-evident certificate hashing — Early Access" },
      { name: "PostgreSQL + PostGIS", category: "Geospatial backend" },
      { name: "Global Card Processing", category: "PCI-DSS payments" },
      { name: "AI Inference Gateway", category: "Model serving layer" }
    ]
  },
  differentiators: {
    badge: "Technical differentiators",
    title: "What makes us different",
    subtitle: "Operational engines and early-access modules built on agronomic science, with beta capabilities clearly labelled. Key modules include transparent benchmark notes where available.",
    footnote: "Sentinel Crop combines satellite, weather and agronomic models in a single workflow.",
    items: [
      {
        title: "ML Yield Prediction (Beta)",
        tag: "Gemini 3 Flash + deterministic models",
        description: "Hybrid yield forecasting combining LLM reasoning with regression models trained on FAOSTAT and regional historical yields worldwide. Field validation in progress.",
        proof: "Backed by 30+ years of global reference data"
      },
      {
        title: "Sentinel-1 SAR Soil Moisture",
        tag: "VV/VH backscatter, real data",
        description: "Radar-derived soil moisture that works through clouds, day or night — not optical-only like most competitors.",
        proof: "Direct Copernicus Sentinel-1 ingestion"
      },
      {
        title: "FAO-56 Penman-Monteith ET₀",
        tag: "Reference evapotranspiration + Dual Kc (Beta)",
        description: "Full FAO-56 Penman-Monteith ET₀ with Dual Kc water balance (Chapter 7) separating soil evaporation from plant transpiration. Dual Kc currently in Beta validation.",
        proof: "ET₀: R² = 0.9390, RMSE = 0.3266 mm/day · n=472 vs Open-Meteo (internal algorithmic coherence)"
      },
      {
        title: "K-Means++ Sub-parcel Zonification",
        tag: "Designed for full parcel coverage",
        description: "Variable-Rate management zones with residual-pixel reassignment — every square metre of the parcel is classified.",
        proof: "Drives VRA fertilization & VRI irrigation maps"
      },
      {
        title: "GDD Phenology Engine",
        tag: "Growing Degree Days, crop-specific",
        description: "Scientific phenological tracking with crop-stage thresholds — used by frost prediction, pest models and harvest forecasting.",
        proof: "Snyder radiative cooling + thermal inversion"
      },
      {
        title: "Crop Verification (4 sources)",
        tag: "Copernicus × NDVI × phenology × land cover",
        description: "Cross-checks the declared crop against four independent data sources — satellite land cover, NDVI temporal profile, phenological signature and regional cadastral references — to detect mismatches before subsidy or sustainability audits.",
        proof: "Region-agnostic: works across Europe, the Americas, Africa and Asia"
      },
      {
        title: "IoT Realtime Monitoring",
        tag: "Supabase Realtime (postgres_changes)",
        description: "Live sensor updates with no manual refresh — soil probes, weather stations and irrigation controllers stream into the dashboard.",
        proof: "Realtime updates through Supabase Realtime"
      }
    ]
  },
  roadmap: {
    badge: "Roadmap & Early Access",
    title: "Modules in active development.",
    subtitle: "These engines are part of the platform but are still being validated against field data and external benchmarks. We label them honestly so you know what is production-grade and what is an early build.",
    footnote: "Design-partner access is granted case-by-case. Roadmap modules are not part of the standard pricing and are not a substitute for licensed insurance, regulated financial products or official emergency-response services.",
    statusEarlyAccess: "Early Access",
    statusBeta: "Beta",
    statusRoadmap: "Roadmap",
    statusDataDependent: "Data-dependent",
    items: [
      {
        title: "Verifiable document trail",
        status: "earlyAccess",
        description: "Generated reports include a SHA-256 Document ID that can be checked through Sentinel Crop's public verification page. Optional public anchoring applies only to selected certificates, not all reports."
      },
      {
        title: "Activity-based carbon estimate",
        status: "dataDependent",
        description: "Estimates emissions from recorded field activities using literature-based emission factors. Satellite vegetation indicators may provide context, but this is not a verified carbon stock estimate or certified carbon credit."
      },
      {
        title: "Parametric insurance modules",
        status: "earlyAccess",
        description: "Scenario modelling for drought, frost and excess rainfall triggers, built for design-partner evaluation. Not a licensed insurance product or payout engine."
      },
      {
        title: "Fire propagation simulation",
        status: "beta",
        description: "Rothermel-based fire spread model with optional Monte-Carlo ensemble for uncertainty bounds. Benchmarked against USDA reference cases; field validation in progress. Treat outputs as decision-support, not as a replacement for civil protection authorities."
      },
      {
        title: "Agricultural credit scoring",
        status: "roadmap",
        description: "Parcel-level risk scoring combining yield history, NDVI stability and water-stress patterns — built for agribusiness lenders and input-finance providers. Roadmap item; we are talking to early design partners."
      },
      {
        title: "Subsidy & compliance report packs",
        status: "beta",
        description: "Pre-formatted report packs for subsidy, traceability and sustainability audits, plus voluntary standards. Format coverage expands per request from cooperatives and agribusiness customers."
      }
    ]
  },
  pwaInstall: {
    title: "Install Sentinel Crop",
    description: "Install our app for quick access and offline functionality",
    install: "Install",
    notNow: "Not now",
    close: "Close"
  },
  meetGaucho: {
    badge: "Meet Gaucho",
    title: "Meet Gaucho",
    subtitle: "Your supervised farm operations assistant.",
    body: "Gaucho connects satellite data, crop context, field observations and evidence-gated recommendations to help you understand what needs attention next.",
    bullets: [
      "Turns parcel data into operational priorities.",
      "Highlights missing evidence before making stronger recommendations.",
      "Keeps sensitive decisions review-only.",
      "Builds a supervised action plan without executing field operations."
    ],
    safetyLine: "Gaucho supports decisions. It does not replace qualified agronomic review or execute field actions.",
    avatarAlt: "Gaucho supervised assistant avatar"
  },
  futureRoadmap: {
    badge: "Long-term roadmap",
    title: "Roadmap: from agronomic intelligence to verified production",
    subtitle: "SentinelCrop's long-term vision is to turn agronomic evidence into commercial trust: agronomy first, evidence provenance second, and evidence-backed procurement later. Current engineering priority remains agronomic intelligence — marketplace work is not an immediate priority.",
    futureBadge: "Future roadmap — not a live feature",
    currentPriorityTitle: "Current technical priority",
    currentPriority: "Agronomic intelligence: satellite / weather / field evidence → FAO-56 water management → Gaucho supervised operational reasoning → evidence-gated reports.",
    priorityTitle: "Near-term sequence (no committed dates)",
    priorityOrder: [
      "FAO-56 / irrigation decision integrity",
      "Canonical irrigation application-target work",
      "Gaucho integration and broader agronomic reasoning",
      "Real prospective field validation when an instrumented parcel is available",
      "Evidence Passport / production provenance",
      "Verified Production Lot",
      "Private Buyer Network",
      "Marketplace",
      "Financial services"
    ],
    stages: [
      {
        title: "Stage 1 — Agronomic intelligence",
        description: "Satellite, weather and field evidence, FAO-56 water management, the supervised Gaucho assistant, risk detection, operational priorities, evidence-gated decisions and defensible reports. Status: current / active development.",
        futureOnly: false
      },
      {
        title: "Stage 2 — Trust & evidence layer",
        description: "Parcel / crop season Evidence Passport, harvest evidence, quality and laboratory evidence, production provenance, the Verified Production Lot concept, tamper-evident hashes and optional public anchoring where appropriate. Anchoring proves record integrity; it does not prove the physical existence of a commodity. Status: future roadmap.",
        futureOnly: true
      },
      {
        title: "Stage 3 — Verified procurement network",
        description: "A private buyer network first: producers and cooperatives make evidence-backed production lots available, and authorised buyers search, filter and match production by crop, origin, quantity, quality, harvest and provenance / evidence status, with requests, matching, buyer requirements and a traceable commercial workflow. This is not an operational marketplace. Status: future roadmap.",
        futureOnly: true
      },
      {
        title: "Stage 4 — Verified production marketplace",
        description: "A possible future multi-buyer / multi-seller marketplace built on evidence-backed production: verified production listings, quality and provenance filters, buyer / seller workflow, and potential logistics, transaction or settlement integrations. Payments and settlement are not implemented, and any settlement rail — including stablecoin or escrow options — would be a non-committed third-party integration. Status: future roadmap.",
        futureOnly: true
      },
      {
        title: "Stage 5 — Financial services",
        description: "Potential future licensed-partner integrations for agricultural insurance support, crop / inventory finance, pre-financing, trade finance and factoring / advances. SentinelCrop is not currently offering regulated financial products. Status: future roadmap.",
        futureOnly: true
      }
    ]
  }
};
